import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Module 6C.1 — Studio signup landing.
 *
 * Three fields, live slug-availability check, then redirect to Google OAuth.
 * The signup_token is set as an httpOnly cookie by /signup/begin; we never
 * touch it from JS. The OAuth callback automatically picks it up.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE GOOGLE AUTH FLOW.
 */
function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

// ---------------------------------------------------------------------------
// Signup-source attribution (frontend-only)
// ---------------------------------------------------------------------------
// Landing CTAs pass ?src=<label>. We stash that in a first-party cookie so
// the origin survives the Google OAuth roundtrip, then read it just before
// handing off to OAuth to fire the Meta Pixel Lead event. No backend contract
// change, no /api/analytics call.
// ---------------------------------------------------------------------------
const SIGNUP_SRC_COOKIE = "snapops_signup_src";
function setSignupSrcCookie(value) {
  if (typeof document === "undefined") return;
  const thirtyDays = 30 * 24 * 60 * 60;
  document.cookie = `${SIGNUP_SRC_COOKIE}=${encodeURIComponent(value)}; path=/; max-age=${thirtyDays}; SameSite=Lax`;
}
function getSignupSrc() {
  if (typeof document === "undefined") return "direct";
  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${SIGNUP_SRC_COOKIE}=`));
  if (!match) return "direct";
  const raw = match.split("=")[1] || "";
  return raw ? decodeURIComponent(raw) : "direct";
}
function clearSignupSrcCookie() {
  if (typeof document === "undefined") return;
  document.cookie = `${SIGNUP_SRC_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export default function Signup() {
  const [studioName, setStudioName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerName, setOwnerName] = useState("");
  const [slugCheck, setSlugCheck] = useState({ state: "idle" });
  const [submitting, setSubmitting] = useState(false);
  const checkTimer = useRef(null);

  // Auto-derive slug from studio name until user edits it explicitly.
  useEffect(() => {
    if (!slugTouched) {
      const derived = slugify(studioName).slice(0, 32);
      setSlug(derived);
    }
  }, [studioName, slugTouched]);

  // Read ?src=<label> on mount and stash it in a first-party cookie so the
  // attribution survives the Google OAuth roundtrip. Absent param → keep any
  // existing cookie untouched (previous landing visit still counts).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const src = params.get("src");
      if (src) setSignupSrcCookie(src);
    } catch (_e) { /* ignore */ }
  }, []);

  // Debounced live slug check.
  useEffect(() => {
    if (checkTimer.current) clearTimeout(checkTimer.current);
    if (!slug) {
      setSlugCheck({ state: "idle" });
      return;
    }
    setSlugCheck({ state: "checking" });
    checkTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/auth/signup/check-slug", { slug });
        if (data.available) {
          setSlugCheck({ state: "ok" });
        } else {
          setSlugCheck({
            state: "bad",
            reason: data.reason,
            suggestions: data.suggestions || [],
          });
        }
      } catch (e) {
        setSlugCheck({ state: "error", reason: "Slug check failed" });
      }
    }, 400);
    return () => checkTimer.current && clearTimeout(checkTimer.current);
  }, [slug]);

  const canSubmit = (
    studioName.trim().length >= 2 &&
    ownerName.trim().length >= 2 &&
    slug.length >= 3 &&
    slugCheck.state === "ok" &&
    !submitting
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // The server sets the `ls_signup_token` httpOnly cookie in the response.
      await api.post("/auth/signup/begin", {
        studio_name: studioName.trim(),
        slug,
        owner_name: ownerName.trim(),
      });
      // Hand off to Google OAuth. The cookie will accompany the post-OAuth
      // /auth/session call automatically.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
      // Fire attribution just before the OAuth hand-off (point of commit).
      const signupSrc = getSignupSrc();
      if (window.fbq) window.fbq("track", "Lead", { source: signupSrc });
      console.log("signup_source", signupSrc);
      clearSignupSrcCookie();
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Signup failed";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4"
      style={{ backgroundColor: "var(--brand-paper)" }}
      data-testid="signup-page"
    >
      <div
        data-testid="signup-card"
        className="fade-up bg-white px-10 py-12 w-[min(480px,94vw)] border border-brand-border"
        style={{ borderRadius: 0 }}
      >
        <div className="text-center mb-8">
          <div className="kicker mb-3" data-testid="signup-kicker">LEAD STUDIO</div>
          <h1 className="font-display text-4xl leading-tight mb-2"
              style={{ color: "var(--brand-ink)" }}
              data-testid="signup-title">Create your studio</h1>
          <p className="text-sm" style={{ color: "var(--brand-muted)" }}>
            One minute to set up. Sign in with Google to claim ownership.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
          <div>
            <label className="block text-[11px] tracking-[0.18em] uppercase text-brand-muted mb-1">
              Studio name
            </label>
            <input
              type="text"
              value={studioName}
              onChange={(e) => setStudioName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 text-sm border border-brand-border bg-white"
              style={{ borderRadius: 0 }}
              placeholder="Lumière Films"
              data-testid="signup-studio-name-input"
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.18em] uppercase text-brand-muted mb-1">
              Slug
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              maxLength={32}
              className="w-full px-3 py-2 text-sm border border-brand-border bg-white"
              style={{ borderRadius: 0 }}
              placeholder="lumiere-films"
              data-testid="signup-slug-input"
              disabled={submitting}
            />
            <SlugIndicator state={slugCheck} onPick={(s) => { setSlugTouched(true); setSlug(s); }} />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.18em] uppercase text-brand-muted mb-1">
              Your name
            </label>
            <input
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              maxLength={80}
              className="w-full px-3 py-2 text-sm border border-brand-border bg-white"
              style={{ borderRadius: 0 }}
              placeholder="Riya Kapoor"
              data-testid="signup-owner-name-input"
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="signup-submit-button"
            className="w-full py-3 px-5 text-[13px] font-medium tracking-wide transition-opacity disabled:opacity-40"
            style={{ backgroundColor: "var(--brand-primary)", color: "#fff", borderRadius: 0 }}
          >
            {submitting ? "Preparing…" : "Continue with Google"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs" style={{ color: "var(--brand-muted)" }}>
          Already have a studio?{" "}
          <a href="/" data-testid="signup-back-to-login"
             style={{ color: "var(--brand-primary)", textDecoration: "underline" }}>
            Sign in →
          </a>
        </div>
      </div>
    </div>
  );
}

function SlugIndicator({ state, onPick }) {
  if (state.state === "idle") {
    return (
      <p className="text-[11px] mt-1.5" style={{ color: "var(--brand-muted)" }}>
        Letters and numbers, separated by hyphens.
      </p>
    );
  }
  if (state.state === "checking") {
    return <p className="text-[11px] mt-1.5 italic" style={{ color: "var(--brand-muted)" }} data-testid="slug-checking">Checking…</p>;
  }
  if (state.state === "ok") {
    return <p className="text-[11px] mt-1.5" style={{ color: "var(--brand-success, #10b981)" }} data-testid="slug-available">✓ Available</p>;
  }
  if (state.state === "bad") {
    return (
      <div className="mt-1.5" data-testid="slug-unavailable">
        <p className="text-[11px]" style={{ color: "var(--brand-danger)" }}>
          {state.reason || "Not available."}
        </p>
        {state.suggestions?.length > 0 && (
          <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
            <span className="text-[10px] tracking-[0.16em] uppercase text-brand-muted">Try:</span>
            {state.suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onPick(s)}
                className="text-[11px] px-2 py-0.5 border border-brand-border hover:bg-brand-paper"
                style={{ borderRadius: 0 }}
                data-testid={`slug-suggestion-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  return null;
}
